import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Types
export interface ArtifactUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

export interface ArtifactWorkspace {
  id: string;
  name: string;
}

export interface ArtifactTopic {
  id: string;
  name: string;
}

export interface Artifact {
  id: string;
  created_at: string;
  updated_at: string | null;
  name: string;
  description: string | null;
  type: string;
  fk_user_id: string;
  fk_workspace_id: string;
  fk_topic_id: string;
  visibility: string;
  content: Record<string, any> | null;
  status: string | null;
  embedding_model: string | null;
  file_size: number | null;
  users: ArtifactUser;
  workspaces: ArtifactWorkspace;
  topics: ArtifactTopic;
  embeddingsCount?: number;
}

export interface ArtifactStats {
  totalArtifacts: number;
  totalEmbeddings: number;
  totalStorageMB: number;
  byType: Record<string, number>;
  byVisibility: Record<string, number>;
  byStatus: Record<string, number>;
  byWorkspace: Array<{ id: string; name: string; count: number }>;
}

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AdminArtifactsFilters {
  type: string | null;
  visibility: string | null;
  status: string | null;
  workspaceId: string | null;
  userId: string | null;
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface AdminArtifactsState {
  artifacts: Artifact[];
  stats: ArtifactStats | null;
  selectedArtifact: Artifact | null;
  pagination: Pagination;
  filters: AdminArtifactsFilters;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  lastUpdated: number | null;
}

const initialPagination: Pagination = {
  page: 1,
  pageSize: 20,
  total: 0,
  totalPages: 0,
};

const initialFilters: AdminArtifactsFilters = {
  type: null,
  visibility: null,
  status: null,
  workspaceId: null,
  userId: null,
  search: '',
  sortBy: 'created_at',
  sortOrder: 'desc',
};

const initialState: AdminArtifactsState = {
  artifacts: [],
  stats: null,
  selectedArtifact: null,
  pagination: initialPagination,
  filters: initialFilters,
  status: 'idle',
  error: null,
  lastUpdated: null,
};

// Async thunks
export const fetchArtifacts = createAsyncThunk(
  'adminArtifacts/fetchArtifacts',
  async (params: {
    page?: number;
    pageSize?: number;
    type?: string;
    visibility?: string;
    status?: string;
    workspaceId?: string;
    userId?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', String(params.page));
    if (params.pageSize) queryParams.append('pageSize', String(params.pageSize));
    if (params.type) queryParams.append('type', params.type);
    if (params.visibility) queryParams.append('visibility', params.visibility);
    if (params.status) queryParams.append('status', params.status);
    if (params.workspaceId) queryParams.append('workspaceId', params.workspaceId);
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await fetch(`/api/admin/artifacts?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch artifacts');
    return response.json();
  }
);

export const fetchArtifactStats = createAsyncThunk(
  'adminArtifacts/fetchStats',
  async () => {
    const response = await fetch('/api/admin/artifacts/stats');
    if (!response.ok) throw new Error('Failed to fetch artifact stats');
    return response.json();
  }
);

export const fetchArtifactDetails = createAsyncThunk(
  'adminArtifacts/fetchDetails',
  async (artifactId: string) => {
    const response = await fetch(`/api/admin/artifacts/${artifactId}`);
    if (!response.ok) throw new Error('Failed to fetch artifact details');
    return response.json();
  }
);

export const updateArtifact = createAsyncThunk(
  'adminArtifacts/update',
  async ({ id, data }: { id: string; data: Partial<Artifact> }) => {
    const response = await fetch(`/api/admin/artifacts/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update artifact');
    }
    return response.json();
  }
);

export const deleteArtifact = createAsyncThunk(
  'adminArtifacts/delete',
  async (artifactId: string) => {
    const response = await fetch(`/api/admin/artifacts/${artifactId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete artifact');
    }
    return { artifactId };
  }
);

// Slice
const adminArtifactsSlice = createSlice({
  name: 'adminArtifacts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<AdminArtifactsFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialFilters;
    },
    setSelectedArtifact: (state, action: PayloadAction<Artifact | null>) => {
      state.selectedArtifact = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch artifacts
    builder.addCase(fetchArtifacts.pending, (state) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(fetchArtifacts.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.artifacts = action.payload.data.artifacts;
      state.pagination = action.payload.data.pagination;
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchArtifacts.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch artifacts';
    });

    // Fetch stats
    builder.addCase(fetchArtifactStats.fulfilled, (state, action) => {
      state.stats = action.payload.data;
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchArtifactStats.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to fetch stats';
    });

    // Fetch details
    builder.addCase(fetchArtifactDetails.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(fetchArtifactDetails.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.selectedArtifact = action.payload.data;
    });
    builder.addCase(fetchArtifactDetails.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch artifact details';
    });

    // Update artifact
    builder.addCase(updateArtifact.fulfilled, (state, action) => {
      const updatedArtifact = action.payload.data;
      const index = state.artifacts.findIndex(a => a.id === updatedArtifact.id);
      if (index !== -1) {
        state.artifacts[index] = { ...state.artifacts[index], ...updatedArtifact };
      }
      if (state.selectedArtifact?.id === updatedArtifact.id) {
        state.selectedArtifact = { ...state.selectedArtifact, ...updatedArtifact };
      }
    });
    builder.addCase(updateArtifact.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to update artifact';
    });

    // Delete artifact
    builder.addCase(deleteArtifact.fulfilled, (state, action) => {
      state.artifacts = state.artifacts.filter(a => a.id !== action.payload.artifactId);
      if (state.selectedArtifact?.id === action.payload.artifactId) {
        state.selectedArtifact = null;
      }
      state.pagination.total -= 1;
    });
    builder.addCase(deleteArtifact.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to delete artifact';
    });
  },
});

export const {
  clearError,
  setFilters,
  resetFilters,
  setSelectedArtifact,
  setPage,
  resetState,
} = adminArtifactsSlice.actions;

export default adminArtifactsSlice.reducer;
